'use client'
import { useTranslation } from 'react-i18next'
import { GeneralMenuBlock, type GeneralMenuBlockProps } from '@/blocks/HeaderBlock/GeneralMenuBlock'
import type { HeaderMenuProps } from '@/blocks/HeaderBlock/HeaderBlock'
import { MenuItemBlock, type MenuItemBlockProps } from '@/blocks/HeaderBlock/MenuItemBlock'
import { ProductMenuBlock, type ProductMenuBlockProps } from '@/blocks/HeaderBlock/ProductMenuBlock'
import { SubMenuBlock, type SubMenuBlockProps } from '@/blocks/HeaderBlock/SubMenuBlock'
import type { GlobalProductMetadata } from '@/components/LayoutWithMenu/fetchProductMetadata'
import { useProductMetadata } from '@/components/LayoutWithMenu/productMetadataHooks'
import { checkBlockType, filterByBlockType, getLinkFieldURL } from '@/services/storyblok/Storyblok.helpers'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { visuallyHidden } from 'ui/src/utils/visibility.css'

type NavLink = { name: string; href: string }

type SeoNavProps = {
  items: HeaderMenuProps
}

function getMenuItemLinks(blok: MenuItemBlockProps['blok']): Array<NavLink> {
  return [{ name: blok.name, href: getLinkFieldURL(blok.link, blok.name) }]
}

function getProductMenuLinks(
  blok: ProductMenuBlockProps['blok'],
  productMetadata: GlobalProductMetadata | null | undefined,
  storeLink: NavLink,
): Array<NavLink> {
  const menuItems = filterByBlockType(blok.menuItems, MenuItemBlock.blockName)
  const productLinks = menuItems.map((item) => {
    const product = productMetadata?.find((p) => p.name === item.name)
    return product
      ? { name: product.displayNameShort, href: product.pageLink }
      : { name: item.name, href: getLinkFieldURL(item.link, item.name) }
  })
  return [...productLinks, storeLink]
}

function getSubMenuLinks(blok: SubMenuBlockProps['blok']): Array<NavLink> {
  return filterByBlockType(blok.menuItems, MenuItemBlock.blockName).map((item) => ({
    name: item.name,
    href: getLinkFieldURL(item.link, item.name),
  }))
}

function getGeneralMenuLinks(blok: GeneralMenuBlockProps['blok']): Array<NavLink> {
  return filterByBlockType(blok.menuItems, MenuItemBlock.blockName).map((item) => ({
    name: item.name,
    href: getLinkFieldURL(item.link, item.name),
  }))
}

/**
 * SEO-only navigation component that renders all navigation links in a visually hidden nav element.
 * This allows search engines to crawl all navigation links.
 */
export function SeoNav({ items }: SeoNavProps) {
  const { t } = useTranslation()
  const locale = useRoutingLocale()
  const productMetadata = useProductMetadata()

  const storeLink: NavLink = { name: t('NAVIGATION_STORE_LINK'), href: PageLink.store({ locale }).toString() }

  const links = items.flatMap((item) => {
    const menuItem = checkBlockType<MenuItemBlockProps['blok']>(item, MenuItemBlock.blockName)
    if (menuItem) return getMenuItemLinks(menuItem)

    const productMenu = checkBlockType<ProductMenuBlockProps['blok']>(item, ProductMenuBlock.blockName)
    if (productMenu) return getProductMenuLinks(productMenu, productMetadata, storeLink)

    const subMenu = checkBlockType<SubMenuBlockProps['blok']>(item, SubMenuBlock.blockName)
    if (subMenu) return getSubMenuLinks(subMenu)

    const generalMenu = checkBlockType<GeneralMenuBlockProps['blok']>(item, GeneralMenuBlock.blockName)
    if (generalMenu) return getGeneralMenuLinks(generalMenu)

    return []
  })

  if (links.length === 0) {
    return null
  }

  return (
    // 'inert' is used to prevent interactions with the nav links like focusing then with tab.
    // It basically states: 'This content exists but it not for user interaction'.
    // It will also remove the element from accessibility tree so aria-hidden is not needed.
    <nav className={visuallyHidden} inert={true}>
      <ul>
        {links.map((link, index) => (
          <li key={`${link.href}-${index}`}>
            <a href={link.href}>{link.name}</a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
